import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

export default function GodUpdateUserInfo() {
  const { t } = useTranslation();

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [userInfo, setUserInfo] = useState({
    userno: "",
    fullname: "",
    reserved_notes: "",
    user_tax_id: "",
    email: "",
    role: "",
  });

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${localStorage.getItem("token")}`;

    fetchAllUsers();
  }, []);

  const clearUserInfo = () => {
    setUserInfo({
      userno: "",
      fullname: "",
      reserved_notes: "",
      user_tax_id: "",
      email: "",
      role: "",
    });
  };

  const loadUserIntoForm = (user) => {
    if (!user) {
      clearUserInfo();
      return;
    }

    setUserInfo({
      userno: user.userno || "",
      fullname: user.fullname || "",
      reserved_notes: user.reserved_notes || "",
      user_tax_id: user.user_tax_id || "",
      email: user.email || "",
      role:
        typeof user.role === "string"
          ? user.role
          : user.role?.value || "",
    });
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/god/users`
      );

      const users = Array.isArray(res.data)
        ? res.data
        : [];

      setAllUsers(users);

      return users;
    } catch (err) {
      console.error(
        "❌ Failed to fetch users",
        err
      );

      setAllUsers([]);

      setMessage(
        "❌ " +
          (
            err.response?.data?.detail ||
            t(
              "god_update_user_info.messages.fetch_failed",
              "Failed to fetch users."
            )
          )
      );

      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUpdateUserInfo = async () => {
    if (!selectedUserId) {
      setMessage(
        "❌ " +
          t(
            "god_update_user_info.messages.missing_user",
            "Please select a user."
          )
      );

      return;
    }

    setSubmitting(true);
    setMessage("");
    setLastAction("update");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/god/update-user-info`,
        {
          user_id: selectedUserId,
          userno: userInfo.userno,
          fullname: userInfo.fullname,
          reserved_notes:
            userInfo.reserved_notes,
          user_tax_id:
            userInfo.user_tax_id,
          email: userInfo.email,
          role: userInfo.role,
        }
      );

      /*
       * Reload the user list after saving so the form reflects
       * the actual committed database state.
       */
      const refreshedUsers =
        await fetchAllUsers();

      const refreshedUser =
        refreshedUsers.find(
          (user) =>
            user.user_id === selectedUserId
        );

      if (refreshedUser) {
        loadUserIntoForm(refreshedUser);
      }

      setMessage(
        "✅ " +
          t(
            "god_update_user_info.messages.success",
            "User information updated successfully."
          )
      );
    } catch (err) {
      console.error(
        "❌ Failed to update user",
        err
      );

      setMessage(
        "❌ " +
          (
            err.response?.data?.detail ||
            t(
              "god_update_user_info.messages.failure",
              "Failed to update user."
            )
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const roleOptions = [
    {
      value: "god",
      label: "god",
    },
    {
      value: "admin",
      label: "admin",
    },
    {
      value: "driver",
      label: "driver",
    },
    {
      value: "payer",
      label: "payer",
    },
  ];

  const userOptions = allUsers.map(
    (user) => ({
      value: user.user_id,
      label:
        `${user.fullname || "-"} | ` +
        `${user.userno || "-"} | ` +
        `${
          typeof user.role === "string"
            ? user.role
            : user.role?.value || "-"
        }`,
      user,
    })
  );

  const selectedUserOption =
    userOptions.find(
      (option) =>
        option.value === selectedUserId
    ) || null;

  return (
    <StandardScreen
      background="default"
      topNav={
        <TopNav
          title={t(
            "god_update_user_info.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div
          style={{
            maxWidth: 720,
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  {t(
                    "god_update_user_info.title"
                  )}
                </h1>

                <FormField
                  label={t(
                    "god_update_user_info.fields.select_user.label"
                  )}
                >
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={loadingUsers}
                    isDisabled={submitting}
                    options={userOptions}
                    value={selectedUserOption}
                    onChange={(option) => {
                      const selected =
                        option?.user || null;

                      setSelectedUserId(
                        selected?.user_id ||
                          null
                      );

                      loadUserIntoForm(
                        selected
                      );

                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t(
                      "god_update_user_info.fields.select_user.placeholder"
                    )}
                  />
                </FormField>

                {selectedUserId && (
                  <>
                    <FormField
                      label={t(
                        "god_update_user_info.fields.userno.label"
                      )}
                    >
                      <TextInput
                        value={
                          userInfo.userno
                        }
                        disabled={submitting}
                        onChange={(e) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              userno:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_info.fields.fullname.label"
                      )}
                    >
                      <TextInput
                        value={
                          userInfo.fullname
                        }
                        disabled={submitting}
                        onChange={(e) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              fullname:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_info.fields.reserved_notes.label",
                        "Notes"
                      )}
                    >
                      <TextInput
                        value={
                          userInfo.reserved_notes
                        }
                        disabled={submitting}
                        onChange={(e) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              reserved_notes:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_info.fields.user_tax_id.label"
                      )}
                    >
                      <TextInput
                        value={
                          userInfo.user_tax_id
                        }
                        disabled={submitting}
                        onChange={(e) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              user_tax_id:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_info.fields.email.label"
                      )}
                    >
                      <TextInput
                        value={
                          userInfo.email
                        }
                        disabled={submitting}
                        onChange={(e) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              email:
                                e.target.value,
                            })
                          )
                        }
                      />
                    </FormField>

                    <FormField
                      label={t(
                        "god_update_user_info.fields.role.label"
                      )}
                    >
                      <Select
                        classNamePrefix="react-select"
                        isDisabled={submitting}
                        options={
                          roleOptions
                        }
                        value={
                          userInfo.role
                            ? {
                                value:
                                  userInfo.role,
                                label:
                                  userInfo.role,
                              }
                            : null
                        }
                        onChange={(option) =>
                          setUserInfo(
                            (current) => ({
                              ...current,
                              role:
                                option?.value ||
                                "",
                            })
                          )
                        }
                        placeholder={t(
                          "god_update_user_info.fields.role.placeholder"
                        )}
                      />
                    </FormField>

                    <Button
                      onClick={
                        handleUpdateUserInfo
                      }
                      disabled={submitting}
                      fullWidth
                    >
                      {submitting
                        ? t(
                            "god_update_user_info.status.submitting",
                            "Updating..."
                          )
                        : lastAction ===
                            "update" &&
                          message.startsWith(
                            "✅"
                          )
                        ? t(
                            "god_update_user_info.status.success_short",
                            "Updated"
                          )
                        : lastAction ===
                            "update" &&
                          message.startsWith(
                            "❌"
                          )
                        ? `${t(
                            "god_update_user_info.status.error_short",
                            "Error"
                          )}: ${message.replace(
                            "❌ ",
                            ""
                          )}`
                        : t(
                            "god_update_user_info.actions.submit"
                          )}
                    </Button>
                  </>
                )}
              </Stack>
            </Card>

            <StatusMessage
              message={message}
            />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}